import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Instagram, MapPin, Pencil, QrCode, Settings, UserPlus } from 'lucide-react';
import ShareIcon from './ui/ShareIcon';
import { supabase } from '../supabaseClient';
import { useToast } from '../contexts/ToastContext';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import { followConnectionsPath } from '../lib/followConnectionsPath';
import {
  invalidateAfterFollow,
  invalidateAfterBlockChange,
} from '../lib/query/invalidateMutations';
import {
  useProfileBundle,
  useProfileEvents,
  useViewerRsvpsForProfile,
} from '../hooks/queries/useProfileQueries';
import { queryKeys } from '../lib/query/queryKeys';
import StaleDataBanner from './ui/StaleDataBanner';
import Avatar from './Avatar';
import PhotoZoomModal from './PhotoZoomModal';
import ReportUserModal from './ReportUserModal';
import { EventCard } from './EventCard';
import { EventListSkeleton } from './EventListSkeleton';
import { NOTIFICATION_TYPES } from '../utils/notifications';
import { useEventRsvpParity } from '../hooks/useEventRsvpParity';
import Modal, { ModalBody, ModalFooter } from './ui/Modal';
import Button from './ui/Button';
import MoreMenu from './ui/MoreMenu';
import { isEventCancelled } from '../utils/eventStatus';
import { isEventOver } from '../utils/eventCutoff';
import { useEventHeadliners } from '../hooks/queries/useEventHeadliners';
import { PopoverMenuItem } from './ui/PopoverMenu';
import { ActionButton, useAddPeople } from './people/useAddPeople';
import { shareLink } from '../lib/shareLink';
import { EVENT_IMAGE_SLOT, getEventImageUrl } from '../lib/images/eventImageUrl';
import './people/People.css';
import { noteHue, ringColor, useHues } from '../lib/map/faceStyle';

function getStartOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** lucide has no TikTok mark: the note, drawn at the Instagram icon's weight. */
function TikTokIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M16 3c.4 2.6 2.1 4.3 4.6 4.6M16 3v11.5a4.5 4.5 0 1 1-4.5-4.5" />
    </svg>
  );
}

export default function Profile() {
  useHues(); // your ring colour (Edit Profile) redraws the big photo
  const { userId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session, authReady } = useCurrentUserProfile();
  const [followLoading, setFollowLoading] = useState(false);
  const eventsRef = useRef(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showUnfollowConfirm, setShowUnfollowConfirm] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [postBlockReportPrompt, setPostBlockReportPrompt] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showUnblockConfirm, setShowUnblockConfirm] = useState(false);
  const userRsvpsRef = useRef([]);
  const [cityName, setCityName] = useState(null);

  const { showToast } = useToast();

  const profileUserId = userId || session?.user?.id;
  const isOwnProfile = session?.user?.id && profileUserId === session.user.id;

  const profileQueryEnabled = authReady && Boolean(profileUserId);

  const {
    data: profileBundle,
    isPending: profilePending,
    isError: profileIsError,
    error: profileQueryError,
    isFetching: profileFetching,
    dataUpdatedAt: profileUpdatedAt,
  } = useProfileBundle(profileUserId, session?.user?.id, { enabled: profileQueryEnabled });

  const {
    data: eventsData,
    isPending: eventsPending,
    isError: eventsIsError,
    isFetching: eventsFetching,
    dataUpdatedAt: eventsUpdatedAt,
  } = useProfileEvents(profileUserId, { enabled: profileQueryEnabled });

  const profile = profileBundle?.profile ?? null;
  // Their row's colour wins over the people-hues list: someone you don't follow yet still shows the colour they picked
  useEffect(() => {
    if (profile) noteHue(profile.display_name, profile.avatar_hue);
  }, [profile?.display_name, profile?.avatar_hue]); // eslint-disable-line react-hooks/exhaustive-deps -- the two fields only
  const isFollowing = profileBundle?.isFollowing ?? false;
  const iBlockedThem = profileBundle?.iBlockedThem ?? false;
  const theyFollowMe = profileBundle?.theyFollowMe ?? false;

  const userRsvps = eventsData?.userRsvps ?? [];
  const eventsMap = useMemo(() => {
    const raw = eventsData?.eventsMap ?? {};
    const normalized = {};
    Object.entries(raw).forEach(([id, ev]) => {
      normalized[id] = {
        ...ev,
        rsvp_going_count: ev.rsvp_going_count ?? ev.going_count ?? 0,
      };
    });
    return normalized;
  }, [eventsData?.eventsMap]);

  const profileEventIds = useMemo(() => Object.keys(eventsMap), [eventsMap]);

  const { data: currentUserRsvps = {}, isFetched: viewerRsvpsReady } = useViewerRsvpsForProfile(
    session?.user?.id,
    profileEventIds,
    {
      enabled:
        authReady && Boolean(session?.user?.id) && !isOwnProfile && profileEventIds.length > 0,
    },
  );

  const ownProfileForEdit = useMemo(() => {
    if (!session?.user?.id || !profile) return null;
    if (profile.id !== session.user.id) return null;
    return profile;
  }, [profile, session?.user?.id]);

  const canOpenEditProfile = Boolean(isOwnProfile && !profilePending && ownProfileForEdit);

  useEffect(() => {
    userRsvpsRef.current = userRsvps;
  }, [userRsvps]);

  const currentUserRsvpsRef = useRef(currentUserRsvps);
  useEffect(() => {
    currentUserRsvpsRef.current = currentUserRsvps;
  }, [currentUserRsvps]);

  const openEditProfile = () => {
    if (canOpenEditProfile) navigate('/profile/edit');
  };

  const handleFollow = async () => {
    if (!session?.user?.id || !profileUserId || followLoading) return;
    setFollowLoading(true);
    try {
      await supabase
        .from('follows')
        .insert({ follower_id: session.user.id, following_id: profileUserId });

      // Create notification for the person being followed
      const displayName = profile?.display_name || session.user.email?.split('@')[0] || 'Someone';

      await supabase.from('notifications').insert({
        user_id: profileUserId, // Person being followed gets the notification
        type: NOTIFICATION_TYPES.FOLLOW,
        title: 'New follower',
        message: `${displayName} started following you`,
        related_user_id: session.user.id,
        read: false,
        created_at: new Date().toISOString(),
      });
      invalidateAfterFollow(queryClient, {
        viewerId: session.user.id,
        targetUserId: profileUserId,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleUnfollow = async () => {
    if (!session?.user?.id || !profileUserId || followLoading) return;
    setFollowLoading(true);
    try {
      await supabase
        .from('follows')
        .delete()
        .eq('follower_id', session.user.id)
        .eq('following_id', profileUserId);
      invalidateAfterFollow(queryClient, {
        viewerId: session.user.id,
        targetUserId: profileUserId,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleConfirmBlock = async () => {
    if (!session?.user?.id || !profileUserId) return;
    const { error } = await supabase.from('user_blocks').insert({
      blocker_id: session.user.id,
      blocked_user_id: profileUserId,
    });
    if (error) {
      console.error('[Profile] block', error);
      showToast('Failed to block user. Please try again.');
      return;
    }
    setShowBlockModal(false);
    setShowProfileMenu(false);
    setPostBlockReportPrompt(true);
    showToast('User blocked.');
    invalidateAfterBlockChange(queryClient, {
      viewerId: session.user.id,
      targetUserId: profileUserId,
    });
  };

  const handleUnblockUser = async () => {
    if (!session?.user?.id || !profileUserId) return;
    const { error } = await supabase
      .from('user_blocks')
      .delete()
      .eq('blocker_id', session.user.id)
      .eq('blocked_user_id', profileUserId);
    if (error) {
      console.error('[Profile] unblock', error);
      showToast('Failed to unblock user. Please try again.');
      return;
    }
    setShowUnblockConfirm(false);
    setShowProfileMenu(false);
    showToast('User unblocked.');
    invalidateAfterBlockChange(queryClient, {
      viewerId: session.user.id,
      targetUserId: profileUserId,
    });
  };

  const handleProfileReportSubmit = async ({ reason, details }) => {
    if (!session?.user?.id || !profileUserId) return;
    const { error } = await supabase.from('reports').insert({
      reporter_user_id: session.user.id,
      reported_user_id: profileUserId,
      group_id: null,
      reason,
      details: details || null,
    });
    if (error) {
      console.error('[Profile] report', error);
      showToast('Failed to submit report. Please try again.');
      return;
    }
    setShowReportModal(false);
    setPostBlockReportPrompt(false);
    showToast('Report submitted. Thank you.');
  };

  const refetchViewerRsvps = async () => {
    if (isOwnProfile && profileUserId) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.profiles.events(profileUserId) });
      return;
    }
    if (!session?.user?.id || profileEventIds.length === 0) return;
    const key = profileEventIds.slice().sort().join(',');
    await queryClient.invalidateQueries({
      queryKey: queryKeys.profiles.viewerRsvps(session.user.id, key),
    });
  };

  const getEventForCutoff = useCallback(
    async (eventId) => {
      const local = eventsMap[eventId];
      if (local) return local;
      const { data } = await supabase
        .from('events')
        .select('event_id, event_datetime, event_date, ends_at, multi_day, venues(venue_tier)')
        .eq('event_id', eventId)
        .maybeSingle();
      return data || null;
    },
    [eventsMap],
  );

  const updateLocalRsvp = useCallback(
    (eventId, newStatus) => {
      const oldStatus = isOwnProfile
        ? userRsvpsRef.current.find((r) => r.event_id === eventId)?.status
        : currentUserRsvpsRef.current[eventId];

      let delta = 0;
      if (oldStatus === 'going' && newStatus !== 'going') delta = -1;
      else if (oldStatus !== 'going' && newStatus === 'going') delta = 1;

      if (isOwnProfile) {
        queryClient.setQueryData(queryKeys.profiles.events(profileUserId), (old) => {
          if (!old) return old;
          const filtered = (old.userRsvps ?? []).filter((r) => r.event_id !== eventId);
          const nextUserRsvps = newStatus
            ? [...filtered, { event_id: eventId, status: newStatus }]
            : filtered;
          let nextEventsMap = old.eventsMap ?? {};
          if (delta !== 0 && nextEventsMap[eventId]) {
            const ev = nextEventsMap[eventId];
            const gc = ev.rsvp_going_count ?? ev.going_count ?? 0;
            const nextGc = Math.max(0, gc + delta);
            nextEventsMap = {
              ...nextEventsMap,
              [eventId]: { ...ev, rsvp_going_count: nextGc, going_count: nextGc },
            };
          }
          return { ...old, userRsvps: nextUserRsvps, eventsMap: nextEventsMap };
        });
        return;
      }

      if (session?.user?.id && profileEventIds.length > 0) {
        const key = profileEventIds.slice().sort().join(',');
        queryClient.setQueryData(queryKeys.profiles.viewerRsvps(session.user.id, key), (old) => {
          const next = { ...(old ?? {}) };
          if (newStatus) next[eventId] = newStatus;
          else delete next[eventId];
          return next;
        });
      }

      if (delta !== 0) {
        queryClient.setQueryData(queryKeys.profiles.events(profileUserId), (old) => {
          if (!old?.eventsMap?.[eventId]) return old;
          const ev = old.eventsMap[eventId];
          const gc = ev.rsvp_going_count ?? ev.going_count ?? 0;
          const nextGc = Math.max(0, gc + delta);
          return {
            ...old,
            eventsMap: {
              ...old.eventsMap,
              [eventId]: { ...ev, rsvp_going_count: nextGc, going_count: nextGc },
            },
          };
        });
      }
    },
    [isOwnProfile, profileUserId, profileEventIds, queryClient, session?.user?.id],
  );

  const { handleEventCardRSVP, parityModals } = useEventRsvpParity({
    session,
    showToast,
    getEventForCutoff,
    updateLocalRsvp,
    refetchRsvps: refetchViewerRsvps,
  });

  // Look up city name from zip code (own profile only — zip is not loaded for others)
  useEffect(() => {
    if (!isOwnProfile || !profile?.zip_code) {
      setCityName(null);
      return;
    }
    const lookup = async () => {
      try {
        const res = await fetch(`https://api.zippopotam.us/us/${profile.zip_code}`);
        if (res.ok) {
          const data = await res.json();
          const place = data?.places?.[0];
          if (place) {
            setCityName(`${place['place name']}, ${place['state abbreviation']}`);
          } else {
            setCityName(null);
          }
        } else {
          setCityName(null);
        }
      } catch {
        setCityName(null);
      }
    };
    lookup();
  }, [isOwnProfile, profile?.zip_code]);

  const displayName = profile?.display_name?.trim() || profile?.email?.split('@')[0] || 'User';
  const todayStart = getStartOfToday();

  const { goingEvents, pastEvents } = useMemo(() => {
    const going = [];
    const past = [];
    userRsvps.forEach((r) => {
      const event = eventsMap[r.event_id];
      if (!event) return;
      if (r.status === 'going') {
        // Going until the event is over (6 AM after its last day), so a festival stays under Going through
        // its second day. A cancelled show is not one you are going to: it files under Past.
        const hasDate = Boolean(event.event_datetime || event.event_date);
        if (hasDate && !isEventOver(event) && !isEventCancelled(event)) going.push(event);
        else past.push(event);
      }
    });
    going.sort((a, b) => new Date(a.event_datetime) - new Date(b.event_datetime));
    past.sort((a, b) => new Date(b.event_datetime) - new Date(a.event_datetime));
    return { goingEvents: going, pastEvents: past };
  }, [userRsvps, eventsMap, todayStart]);

  const { data: tabHeadliners = {} } = useEventHeadliners(goingEvents.map((e) => e.event_id));

  const userRsvpByEventId = useMemo(() => {
    const map = {};
    userRsvps.forEach((r) => {
      map[r.event_id] = r.status;
    });
    return map;
  }, [userRsvps]);

  const getCardUserStatus = useCallback(
    (eventId) => {
      if (!session?.user?.id) return null;
      if (isOwnProfile) return userRsvpByEventId[eventId];
      if (!viewerRsvpsReady) return undefined;
      return currentUserRsvps[eventId] ?? null;
    },
    [session?.user?.id, isOwnProfile, userRsvpByEventId, viewerRsvpsReady, currentUserRsvps],
  );

  const getCardRsvpOldStatus = useCallback(
    (eventId) => {
      if (isOwnProfile) return userRsvpByEventId[eventId] ?? null;
      return currentUserRsvps[eventId] ?? null;
    },
    [isOwnProfile, userRsvpByEventId, currentUserRsvps],
  );

  const { openCode, sheets: addPeopleSheets } = useAddPeople();

  // Tiles, two across, like the Events feed (Sam, 2026-10-28).
  const renderTiles = (events) => (
    <div className="event-tiles">
      {events.map((event) => (
        <div
          key={event.event_id}
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/events/${event.event_id}`)}
          onKeyDown={(e) => e.key === 'Enter' && navigate(`/events/${event.event_id}`)}
          className="cursor-pointer"
        >
          <EventCard
            event={{ ...event, rsvp_going_count: event.going_count ?? 0 }}
            variant="tile"
            headliners={tabHeadliners[event.event_id] ?? null}
            userStatus={getCardUserStatus(event.event_id)}
            onRSVP={(eventId, status) =>
              handleEventCardRSVP(eventId, status, getCardRsvpOldStatus(eventId))
            }
          />
        </div>
      ))}
    </div>
  );

  if (!userId && authReady && !session?.user?.id) {
    navigate('/login', { replace: true });
    return null;
  }
  if (!profileUserId) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-center py-24 text-gray-500 dark:text-dark-muted">
          Loading...
        </div>
      </div>
    );
  }

  const profileLoading = profilePending && !profile;
  const eventsLoading =
    eventsPending && userRsvps.length === 0 && Object.keys(eventsMap).length === 0;
  const showProfileStaleBanner = Boolean(profile) && profileIsError;
  const showEventsStaleBanner = (goingEvents.length > 0 || pastEvents.length > 0) && eventsIsError;
  const profileStaleUpdatedAt = Math.max(profileUpdatedAt || 0, eventsUpdatedAt || 0);
  const profileStaleFetching = profileFetching || eventsFetching;

  if (profileLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-center py-24 text-gray-500 dark:text-dark-muted">
          Loading profile...
        </div>
      </div>
    );
  }

  if (!profile) {
    const message = profileIsError
      ? profileQueryError?.message || 'Failed to load profile.'
      : 'Profile not found.';
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="rounded-lg bg-danger-fill text-danger p-4">{message}</div>
        <Link to="/" className="mt-4 inline-block text-primary font-medium hover:underline">
          Back to Events
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <StaleDataBanner
        visible={showProfileStaleBanner || showEventsStaleBanner}
        dataUpdatedAt={profileStaleUpdatedAt}
        isFetching={profileStaleFetching}
        className="mb-4"
      />
      {/* Header (Stage 5, Sam's round-3 pick): photo, name, place, Instagram, then Followers / Following / Events */}
      <div className="mb-5 text-center">
        <div className="flex justify-center">
          <button
            type="button"
            className="pp-avatar disabled:cursor-default"
            style={{ '--pp-ring': ringColor(displayName) }}
            disabled={isOwnProfile ? !canOpenEditProfile : !profile?.profile_photo_url}
            onClick={isOwnProfile ? openEditProfile : () => setShowPhotoModal(true)}
            aria-label={isOwnProfile ? 'Edit profile' : 'View photo'}
          >
            <Avatar
              displayName={displayName}
              profilePhotoUrl={profile?.profile_photo_url}
              size="lg"
            />
          </button>
        </div>
        <h1 className="mt-3 truncate text-2xl font-bold text-gray-900 dark:text-dark-text">
          {displayName}
        </h1>
        {/* Real name and place under the display name, when we have them (Sam, 2026-10-30; the same twice is fine) */}
        {(() => {
          const realName = [profile.first_name, profile.last_name]
            .map((x) => x?.trim())
            .filter(Boolean)
            .join(' ');
          const place = isOwnProfile && profile.zip_code ? cityName || profile.zip_code : null;
          if (!realName && !place) return null;
          return (
            <p className="mt-0.5 inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-dark-muted">
              {realName && <span className="truncate">{realName}</span>}
              {realName && place && <span aria-hidden>·</span>}
              {place && (
                <>
                  <MapPin className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
                  <span className="truncate">{place}</span>
                </>
              )}
            </p>
          );
        })()}
        {profile.instagram_handle || profile.tiktok_handle ? (
          <div className="pp-socials">
            {profile.instagram_handle && (
              <a
                className="pp-ig"
                href={`https://instagram.com/${encodeURIComponent(profile.instagram_handle)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Instagram className="h-3.5 w-3.5" aria-hidden />
                {profile.instagram_handle}
              </a>
            )}
            {profile.tiktok_handle && (
              <a
                className="pp-ig"
                href={`https://www.tiktok.com/@${encodeURIComponent(profile.tiktok_handle)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <TikTokIcon className="h-3.5 w-3.5" />
                {profile.tiktok_handle}
              </a>
            )}
          </div>
        ) : null}
        <div className="pp-chips mt-6">
          <button
            type="button"
            className="pp-chip"
            onClick={() =>
              navigate(
                isOwnProfile
                  ? '/people?tab=followers'
                  : followConnectionsPath('followers', profileUserId),
              )
            }
          >
            <b>{profile.followers_count ?? 0}</b>
            <span>Followers</span>
          </button>
          <button
            type="button"
            className="pp-chip"
            onClick={() =>
              navigate(
                isOwnProfile
                  ? '/people?tab=following'
                  : followConnectionsPath('following', profileUserId),
              )
            }
          >
            <b>{profile.following_count ?? 0}</b>
            <span>Following</span>
          </button>
          <button
            type="button"
            className="pp-chip"
            onClick={() =>
              eventsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          >
            <b>{goingEvents.length + pastEvents.length}</b>
            <span>Events</span>
          </button>
        </div>
      </div>

      {/* The lit action first, Share second, on both profiles (Sam, 2026-10-29). No Message until chat exists. */}
      {isOwnProfile ? (
        <div className="mb-8 flex justify-center gap-2 px-2">
          <ActionButton
            icon={<UserPlus className="w-4 h-4" />}
            label="Friends"
            on
            onClick={() => navigate('/people')}
          />
          <ActionButton icon={<QrCode className="w-4 h-4" />} label="Share" onClick={openCode} />
          <ActionButton
            icon={<Pencil className="w-4 h-4" />}
            label="Edit"
            disabled={!canOpenEditProfile}
            onClick={openEditProfile}
          />
          <ActionButton
            icon={<Settings className="w-4 h-4" />}
            label="Settings"
            onClick={() => navigate('/settings')}
          />
        </div>
      ) : session ? (
        <div className="mb-8 flex justify-center gap-2 px-2">
          <ActionButton
            icon={isFollowing ? <Check className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
            label={isFollowing ? 'Following' : theyFollowMe ? 'Follow back' : 'Follow'}
            on={!isFollowing}
            disabled={followLoading}
            onClick={isFollowing ? () => setShowUnfollowConfirm(true) : handleFollow}
          />
          <ActionButton
            icon={<ShareIcon className="w-4 h-4" />}
            label="Share"
            onClick={() => shareLink({ url: `/profile/${profileUserId}`, text: displayName })}
          />
          <div className="pp-act">
            <MoreMenu
              open={showProfileMenu}
              onOpenChange={setShowProfileMenu}
              ariaLabel="Profile actions"
              triggerVariant="icon"
              triggerSize="md"
              menuClassName="rounded-xl"
            >
              {isFollowing ? (
                <PopoverMenuItem
                  onClick={() => {
                    setShowProfileMenu(false);
                    setShowUnfollowConfirm(true);
                  }}
                  disabled={followLoading}
                  className="text-danger disabled:opacity-50"
                >
                  Unfollow
                </PopoverMenuItem>
              ) : null}
              {!iBlockedThem ? (
                <PopoverMenuItem
                  onClick={() => {
                    setShowProfileMenu(false);
                    setShowBlockModal(true);
                  }}
                  className="text-danger"
                >
                  Block
                </PopoverMenuItem>
              ) : null}
              {iBlockedThem ? (
                <PopoverMenuItem
                  onClick={() => {
                    setShowProfileMenu(false);
                    setShowUnblockConfirm(true);
                  }}
                >
                  Unblock
                </PopoverMenuItem>
              ) : null}
              <PopoverMenuItem
                onClick={() => {
                  setShowProfileMenu(false);
                  setShowReportModal(true);
                }}
              >
                Report
              </PopoverMenuItem>
            </MoreMenu>
            <span>More</span>
          </div>
        </div>
      ) : null}

      {/* Going, soonest first (the next show is just the first tile) */}
      <div ref={eventsRef} className="scroll-mt-20">
        {/* Only the sections that have something: Past alone for someone going to nothing, and the other way round */}
        {eventsLoading ? (
          <EventListSkeleton count={3} />
        ) : goingEvents.length === 0 && pastEvents.length === 0 ? (
          <p className="py-6 text-center text-gray-500 dark:text-dark-muted">No events yet.</p>
        ) : goingEvents.length > 0 ? (
          <>
            <p className="pp-label">Going</p>
            {renderTiles(goingEvents)}
          </>
        ) : null}
        {/* Past: small tiles, four across, name only - always open, no toggle (Sam, 2026-10-30) */}
        {pastEvents.length > 0 && (
          <>
            <p className={`pp-label ${goingEvents.length > 0 ? 'mt-6' : ''}`}>Past</p>
            <div className="pp-past">
              {pastEvents.map((e) => {
                const src =
                  getEventImageUrl(e, EVENT_IMAGE_SLOT.CARD) ||
                  getEventImageUrl(e, EVENT_IMAGE_SLOT.HERO);
                return (
                  <button
                    key={e.event_id}
                    type="button"
                    className="pp-past__i"
                    style={src ? { backgroundImage: `url(${src})` } : undefined}
                    onClick={() => navigate(`/events/${e.event_id}`)}
                  >
                    <span>{e.display_name || e.event_name}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      <PhotoZoomModal
        open={showPhotoModal}
        onClose={() => setShowPhotoModal(false)}
        imageUrl={profile?.profile_photo_url}
        alt={displayName}
      />

      {parityModals}

      <Modal
        isOpen={showUnfollowConfirm}
        onClose={() => setShowUnfollowConfirm(false)}
        title={`Unfollow ${displayName}?`}
        size="sm"
      >
        <ModalFooter align="split">
          <Button variant="secondary" onClick={() => setShowUnfollowConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            loading={followLoading}
            onClick={async () => {
              await handleUnfollow();
              setShowUnfollowConfirm(false);
            }}
          >
            Unfollow
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={showBlockModal}
        onClose={() => setShowBlockModal(false)}
        title="Block User?"
        size="md"
      >
        <ModalBody>
          <p className="text-ink/70">
            Blocking this user will prevent them from following you or messaging you, and hides them
            from your experience where applicable.
          </p>
        </ModalBody>
        <ModalFooter align="split">
          <Button variant="secondary" onClick={() => setShowBlockModal(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleConfirmBlock}>
            Block User
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={postBlockReportPrompt}
        onClose={() => setPostBlockReportPrompt(false)}
        title="Do you also want to report this user?"
        size="md"
        closeOnVeil={false}
      >
        <ModalFooter align="split">
          <Button variant="secondary" onClick={() => setPostBlockReportPrompt(false)}>
            No
          </Button>
          <Button
            onClick={() => {
              setPostBlockReportPrompt(false);
              setShowReportModal(true);
            }}
          >
            Yes
          </Button>
        </ModalFooter>
      </Modal>

      <ReportUserModal
        open={showReportModal}
        onClose={() => setShowReportModal(false)}
        onSubmit={handleProfileReportSubmit}
      />

      <Modal
        isOpen={showUnblockConfirm}
        onClose={() => setShowUnblockConfirm(false)}
        title="Unblock this user?"
        size="sm"
      >
        <ModalFooter align="split">
          <Button variant="secondary" onClick={() => setShowUnblockConfirm(false)}>
            Cancel
          </Button>
          <Button onClick={handleUnblockUser}>Unblock</Button>
        </ModalFooter>
      </Modal>
      {addPeopleSheets}
    </div>
  );
}
